
import { useEffect, useMemo, useRef, useState } from 'react';
import FloatingSmileyDiscoverDialog from '@/components/help-center/FloatingSmileyDiscoverDialog';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { smilieProxyUrl } from '@/lib/smilie-proxy-url';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

function powerImageSrc(code: string): string {
  return smilieProxyUrl(code, 40);
}

type HeroFloat = {
  code: string;
  top: string;
  left: string;
  size: number;
  delay: number;
  duration: number;
  opacity: number;
  rev?: boolean;
};

/**
 * Grid de posições:
 * - Zona superior (top 6–22%): espaço vazio acima do título — populado com powers
 * - Laterais (left < 18% ou left > 82%): visíveis em qualquer altura
 * - Zona inferior (top > 82%): abaixo da searchbar e exemplos — visíveis no centro
 * - Evitado: centro horizontal (20–80%) entre top 30–80% pois sobrepõe título/desc/searchbar
 */
const POSITION_GRID: Omit<HeroFloat, 'code'>[] = [
  // — Zona superior: acima do título, abaixo do navbar (navbar ocupa ~13% do hero)
  { top: '17%', left: '30%',  size: 46, delay: 0.3, duration: 5.5, opacity: 0.35 },
  { top: '22%', left: '58%',  size: 50, delay: 0.9, duration: 6.1, opacity: 0.30, rev: true },
  { top: '15%', left: '75%',  size: 42, delay: 1.4, duration: 5.7, opacity: 0.32 },
  { top: '25%', left: '42%',  size: 44, delay: 0.6, duration: 5.9, opacity: 0.28, rev: true },
  { top: '14%', left: '18%',  size: 38, delay: 1.7, duration: 5.4, opacity: 0.33 },
  { top: '19%', left: '88%',  size: 48, delay: 0.2, duration: 6.3, opacity: 0.31, rev: true },
  // — Lateral esquerda
  { top: '28%', left: '8%',   size: 48, delay: 0.8, duration: 5.5, opacity: 0.40 },
  { top: '44%', left: '4%',   size: 38, delay: 0.2, duration: 6.4, opacity: 0.32 },
  { top: '42%', left: '14%',  size: 42, delay: 0.3, duration: 5.6, opacity: 0.34, rev: true },
  { top: '58%', left: '10%',  size: 44, delay: 1.2, duration: 6.0, opacity: 0.33 },
  { top: '62%', left: '12%',  size: 44, delay: 0.0, duration: 5.2, opacity: 0.38 },
  { top: '70%', left: '20%',  size: 50, delay: 2.2, duration: 5.7, opacity: 0.39, rev: true },
  { top: '78%', left: '11%',  size: 42, delay: 1.8, duration: 5.4, opacity: 0.36 },
  // — Lateral direita
  { top: '30%', left: '92%',  size: 48, delay: 0.4, duration: 6.1, opacity: 0.34, rev: true },
  { top: '34%', left: '86%',  size: 42, delay: 1.1, duration: 5.8, opacity: 0.36, rev: true },
  { top: '38%', left: '95%',  size: 44, delay: 1.5, duration: 5.3, opacity: 0.35, rev: true },
  { top: '51%', left: '84%',  size: 36, delay: 2.0, duration: 6.2, opacity: 0.33, rev: true },
  { top: '50%', left: '90%',  size: 62, delay: 1.7, duration: 5.4, opacity: 0.37 },
  { top: '64%', left: '91%',  size: 40, delay: 0.9, duration: 6.0, opacity: 0.34, rev: true },
  { top: '79%', left: '78%',  size: 54, delay: 0.5, duration: 6.3, opacity: 0.35 },
  // — Zona inferior: abaixo da searchbar (visíveis no centro)
  { top: '85%', left: '55%',  size: 38, delay: 0.6, duration: 5.8, opacity: 0.30 },
  { top: '83%', left: '35%',  size: 52, delay: 2.1, duration: 5.9, opacity: 0.32 },
  { top: '88%', left: '68%',  size: 50, delay: 0.7, duration: 6.2, opacity: 0.34, rev: true },
  { top: '91%', left: '48%',  size: 44, delay: 1.3, duration: 5.6, opacity: 0.29 },
];

function generateHeroFloats(pool: string[]): HeroFloat[] {
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return POSITION_GRID.map((pos, i) => ({
    code: shuffled[i % shuffled.length],
    ...pos,
  }));
}

export default function HeroFloatingSmilies() {
  const { powers: catalogPowers, hydrated } = useCatalogPowers();
  const [broken, setBroken] = useState<Record<number, boolean>>({});
  const [floats, setFloats] = useState<HeroFloat[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const powerPool = useMemo(
    () => catalogPowers.map((p) => p.slug),
    [catalogPowers],
  );
  const floatsGeneratedRef = useRef(false);

  const selectedPower = useMemo(() => {
    if (!selectedCode) return null;
    return catalogPowers.find((p) => p.slug === selectedCode.toLowerCase()) ?? null;
  }, [selectedCode, catalogPowers]);

  useEffect(() => {
    if (!hydrated || powerPool.length === 0 || floatsGeneratedRef.current) return;
    floatsGeneratedRef.current = true;
    setFloats(generateHeroFloats(powerPool));
  }, [hydrated, powerPool]);

  function handleSmileyClick(code: string) {
    setSelectedCode(code);
    setDialogOpen(true);
  }

  function handleDialogOpenChange(open: boolean) {
    setDialogOpen(open);
    if (!open) setSelectedCode(null);
  }

  return (
    <TooltipProvider delayDuration={250}>
      <FloatingSmileyDiscoverDialog
        smileyCode={selectedCode}
        power={selectedPower}
        open={dialogOpen}
        onOpenChange={handleDialogOpenChange}
      />

      <div className="pointer-events-none absolute inset-0 z-[15] overflow-hidden">
        {floats.map((s, idx) => {
          if (broken[idx]) return null;
          return (
            <div
              key={idx}
              className="pointer-events-none absolute"
              style={{
                top: s.top,
                left: s.left,
                transform: 'translate(-50%, -50%)',
                opacity: s.opacity,
              }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'pointer-events-auto cursor-pointer border-0 bg-transparent p-0 shadow-none outline-none',
                      'transition-transform duration-200 hover:scale-110 active:scale-95',
                      'focus-visible:ring-2 focus-visible:ring-sky-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]'
                    )}
                    aria-label={`Descobrir power: ${s.code}`}
                    onClick={() => handleSmileyClick(s.code)}
                  >
                    <div
                      className={`will-change-transform ${s.rev ? 'animate-hero-smiley-float-rev' : 'animate-hero-smiley-float'}`}
                      style={{
                        width: s.size,
                        animationDelay: `${s.delay}s`,
                        animationDuration: `${s.duration}s`,
                      }}
                    >
                      <img
                        src={powerImageSrc(s.code)}
                        alt={s.code}
                        width={s.size}
                        height={s.size}
                        className="h-auto w-full max-w-none select-none object-contain drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)]"
                        loading="lazy"
                        draggable={false}
                        onError={() => setBroken((b) => ({ ...b, [idx]: true }))}
                      />
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="z-[100] font-poppins text-xs capitalize">
                  {catalogPowers.find((p) => p.slug === s.code)?.name ?? s.code}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
